/*
* Copyright (c) 2009-2021. Authors: see NOTICE file.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*      http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

/* eslint no-undef: 0, no-new-func: 0 */

import ZoomifySource from 'ol/source/Zoomify';
import {createFromTileUrlFunctions} from 'ol/tileurlfunction';
import {DEFAULT_TILE_SIZE} from 'ol/tilegrid/common';

export const DEFAULT_FLUORESCENCE_MAX = 64;

export const DEFAULT_FLUORESCENCE_COLORS = [
  '#00ff00',
  '#ff0000',
  '#0000ff',
  '#ffff00',
  '#ff00ff',
  '#00ffff',
  '#ffffff'
];

function clampByteFunc(value) {
  if(value < 0) {
    return 0;
  }
  if(value > 255) {
    return 255;
  }
  return Math.round(value);
}

function clampUnitFunc(value) {
  if(value < 0) {
    return 0;
  }
  if(value > 1) {
    return 1;
  }
  return value;
}

function colorToHexNumberFunc(color) {
  if(typeof color === 'number') {
    return color;
  }
  if(!color || typeof color !== 'string') {
    return 0xffffff;
  }

  let normalized = color.charAt(0) === '#' ? color.slice(1) : color;
  if(normalized.length === 3) {
    normalized = normalized.split('').map(value => value + value).join('');
  }

  let parsed = parseInt(normalized.slice(0, 6), 16);
  return isNaN(parsed) ? 0xffffff : parsed;
}

function colorToRgbFunc(color) {
  let parsed = colorToHexNumber(color);
  return [
    (parsed >> 16) & 255,
    (parsed >> 8) & 255,
    parsed & 255
  ];
}

function pixelGrayFunc(pixel) {
  return pixel[0] * Pr + pixel[1] * Pg + pixel[2] * Pb;
}

function normalizeGrayFunc(pixel, min, max) {
  min = Number(min);
  max = Number(max);

  if(isNaN(min)) {
    min = 0;
  }
  if(isNaN(max)) {
    max = 255;
  }
  if(max <= min) {
    max = min + 1;
  }

  let gray = pixelGray(pixel);
  return clampUnit((gray - min) / (max - min));
}

function functionLiteral(value) {
  if(typeof value === 'boolean') {
    return value ? 'true' : 'false';
  }

  let numericValue = Number(value);
  return isNaN(numericValue) ? '0' : String(numericValue);
}

export function createFluorescenceLookup(values, fallback) {
  let cases = values.map((value, index) => `case ${index}: return ${functionLiteral(value)};`).join('');
  return new Function('index', `switch(index) {${cases} default: return ${functionLiteral(fallback)};}`);
}

export let fluorescenceConstLib = {
  Pr: 0.299,
  Pg: 0.587,
  Pb: 0.114,

  clampByte: clampByteFunc,
  clampUnit: clampUnitFunc,
  colorToHexNumber: colorToHexNumberFunc,
  colorToRgb: colorToRgbFunc,
  normalizeGray: normalizeGrayFunc,
  pixelGray: pixelGrayFunc
};

export function fluorescenceOperation(pixels) {
  let output = [0, 0, 0, 255];

  for(let i = 0; i < pixels.length; i++) {
    if(channelVisible(i) === false) {
      continue;
    }

    let color = colorToRgb(channelColor(i));
    let intensity = normalizeGray(pixels[i], channelMin(i), channelMax(i));

    output[0] = clampByte(output[0] + color[0] * intensity);
    output[1] = clampByte(output[1] + color[1] * intensity);
    output[2] = clampByte(output[2] + color[2] * intensity);
  }

  return output;
}

export function singleGrayscaleOperation(pixels) {
  let gray = clampByte(normalizeGray(pixels[0], singleMin, singleMax) * 255);
  return [gray, gray, gray, 255];
}

function computeZoomifyMetadata(size, tileSize) {
  const imageWidth = size[0];
  const imageHeight = size[1];
  const tierSizeInTiles = [];
  let tileSizeForTierSizeCalculation = tileSize;

  while(imageWidth > tileSizeForTierSizeCalculation || imageHeight > tileSizeForTierSizeCalculation) {
    tierSizeInTiles.push([
      Math.ceil(imageWidth / tileSizeForTierSizeCalculation),
      Math.ceil(imageHeight / tileSizeForTierSizeCalculation)
    ]);
    tileSizeForTierSizeCalculation += tileSizeForTierSizeCalculation;
  }
  tierSizeInTiles.push([1, 1]);
  tierSizeInTiles.reverse();

  const tileCountUpToTier = [0];
  for(let i = 1; i < tierSizeInTiles.length; i++) {
    tileCountUpToTier.push(
      tierSizeInTiles[i - 1][0] * tierSizeInTiles[i - 1][1] + tileCountUpToTier[i - 1]
    );
  }

  return {tierSizeInTiles, tileCountUpToTier};
}

function createFromTemplate(source, tierSizeInTiles, tileCountUpToTier) {
  return template => {
    return tileCoord => {
      if(!tileCoord) {
        return undefined;
      }

      const tileCoordZ = tileCoord[0];
      const tileCoordX = tileCoord[1];
      const tileCoordY = -tileCoord[2] - 1;
      const tileIndex = tileCoordX + tileCoordY * tierSizeInTiles[tileCoordZ][0];
      const tileSize = source.tileGrid.getTileSize(tileCoordZ);
      const tileWidth = Array.isArray(tileSize) ? tileSize[0] : tileSize;
      const tileGroup = ((tileIndex + tileCountUpToTier[tileCoordZ]) / tileWidth) | 0;
      const localContext = {
        z: tileCoordZ,
        x: tileCoordX,
        y: tileCoordY,
        tileIndex,
        TileGroup: 'TileGroup' + tileGroup
      };

      return template.replace(/\{(\w+?)\}/g, function(match, key) {
        return localContext[key];
      });
    };
  };
}

export function createCytomineZoomifySource({
  urls,
  projection,
  size,
  extent,
  crossOrigin = 'Anonymous',
  transition = 0,
  tileSize = DEFAULT_TILE_SIZE
}) {
  let source = new ZoomifySource({
    crossOrigin,
    projection,
    url: urls[0],
    size,
    extent,
    transition,
    tileSize
  });

  let {tierSizeInTiles, tileCountUpToTier} = computeZoomifyMetadata(size, tileSize);
  let tileUrlFunction = createFromTileUrlFunctions(
    urls.map(createFromTemplate(source, tierSizeInTiles, tileCountUpToTier))
  );
  source.setTileUrlFunction(tileUrlFunction, urls.join('\n'));

  return source;
}
